import { useState, useEffect } from 'react';
import axios from 'axios';
import { applyPortfolioColors } from '../utils/applyColors';

const API_BASE = '/api/content';

/**
 * Hook to fetch and manage projects
 */
export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/projects`);
      if (response.data.success) {
        setProjects(response.data.data || []);
        setError(null);
      } else {
        setError('Failed to fetch projects');
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
      // Set empty array on error to prevent crashes
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  return { projects, loading, error, refetch: fetchProjects };
}

/**
 * Hook to fetch and manage about information
 */
export function useAbout() {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/simple-content?type=about`);
      if (response.data.success) {
        setAbout(response.data.data);
        setError(null);
      } else {
        setError('Failed to fetch about information');
      }
    } catch (err) {
      console.error('Error fetching about:', err);
      setError('Failed to load about information');
    } finally {
      setLoading(false);
    }
  };

  return { about, loading, error, refetch: fetchAbout };
}

/**
 * Hook to fetch and manage skills
 */
export function useSkills(type) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (type) {
      fetchSkills(type);
    }
  }, [type]);

  const fetchSkills = async (skillType) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/simple-content?type=skills&subtype=${skillType}`);
      if (response.data.success) {
        setSkills(response.data.data || []);
        setError(null);
      } else {
        setError('Failed to fetch skills');
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError('Failed to load skills');
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  return { skills, loading, error, refetch: () => fetchSkills(type) };
}

/**
 * Hook to fetch and manage social links
 */
export function useSocialLinks() {
  const [socialLinks, setSocialLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/simple-content?type=social`);
      if (response.data.success) {
        setSocialLinks(response.data.data);
        setError(null);
      } else {
        setError('Failed to fetch social links');
      }
    } catch (err) {
      console.error('Error fetching social links:', err);
      setError('Failed to load social links');
    } finally {
      setLoading(false);
    }
  };

  return { socialLinks, loading, error, refetch: fetchSocialLinks };
}

/**
 * Hook to fetch and manage work experience
 */
export function useWorkExperience() {
  const [workExperience, setWorkExperience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWorkExperience();
  }, []);

  const fetchWorkExperience = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/experience-education?type=work-experience`);
      if (response.data.success) {
        // Sort by: NULL end_date first (current positions), then end_date DESC, then start_date DESC
        const sorted = (response.data.data || []).sort((a, b) => {
          // Current positions (no end_date) come first
          const aIsCurrent = !a.end_date;
          const bIsCurrent = !b.end_date;
          
          if (aIsCurrent && !bIsCurrent) return -1;
          if (!aIsCurrent && bIsCurrent) return 1;
          
          // If both are current or both are past, sort by end_date DESC
          if (!aIsCurrent && !bIsCurrent) {
            const endDateA = new Date(a.end_date);
            const endDateB = new Date(b.end_date);
            const endDateDiff = endDateB - endDateA; // DESC
            if (endDateDiff !== 0) return endDateDiff;
          }
          
          // If same end date (or both current), sort by start_date DESC
          const startDateA = new Date(a.start_date);
          const startDateB = new Date(b.start_date);
          return startDateB - startDateA; // DESC
        });
        setWorkExperience(sorted);
        setError(null);
      } else {
        setError('Failed to fetch work experience');
      }
    } catch (err) {
      console.error('Error fetching work experience:', err);
      setError('Failed to load work experience');
      setWorkExperience([]);
    } finally {
      setLoading(false);
    }
  };

  return { workExperience, loading, error, refetch: fetchWorkExperience };
}

/**
 * Hook to fetch and manage education
 */
export function useEducation() {
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEducation();
  }, []);

  const fetchEducation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/experience-education?type=education`);
      if (response.data.success) {
        // Sort by: NULL end_date first (in-progress), then end_date DESC, then start_date DESC
        const sorted = (response.data.data || []).sort((a, b) => {
          const aIsInProgress = !a.end_date;
          const bIsInProgress = !b.end_date;
          
          if (aIsInProgress && !bIsInProgress) return -1;
          if (!aIsInProgress && bIsInProgress) return 1;
          
          if (!aIsInProgress && !bIsInProgress) {
            const endDateA = new Date(a.end_date);
            const endDateB = new Date(b.end_date);
            const endDateDiff = endDateB - endDateA;
            if (endDateDiff !== 0) return endDateDiff;
          }
          
          const startDateA = new Date(a.start_date);
          const startDateB = new Date(b.start_date);
          return startDateB - startDateA;
        });
        setEducation(sorted);
        setError(null);
      } else {
        setError('Failed to fetch education');
      }
    } catch (err) {
      console.error('Error fetching education:', err);
      setError('Failed to load education');
      setEducation([]);
    } finally {
      setLoading(false);
    }
  };

  return { education, loading, error, refetch: fetchEducation };
}

/**
 * Hook to fetch and manage portfolio settings (images, text)
 */
export function usePortfolioSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchSettings();
  }, [refreshKey]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Only use cache-busting if explicitly needed (e.g., after updates)
      const response = await axios.get(`${API_BASE}/portfolio-settings`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Normalize null/undefined/empty string values to defaults
        const normalizeImage = (value, defaultValue) => {
          if (!value) return defaultValue;
          if (typeof value !== 'string') return defaultValue;
          const trimmed = value.trim();
          return trimmed === '' || trimmed === 'null' || trimmed === 'undefined' ? defaultValue : value;
        };

        const normalizedData = data ? {
          ...data,
          home_main_image: normalizeImage(data.home_main_image, '/Assets/home-main.png'),
          home_about_image: normalizeImage(data.home_about_image, '/Assets/about.png'),
          about_avatar_image: normalizeImage(data.about_avatar_image, '/Assets/avatar.png'),
          navbar_logo: normalizeImage(data.navbar_logo, '/Assets/logo.png'),
          // Resume PDF URL - keep as is if provided, otherwise null (component will use default)
          resume_pdf_url: data.resume_pdf_url || null,
        } : {
          home_main_image: '/Assets/home-main.png',
          home_about_image: '/Assets/about.png',
          about_avatar_image: '/Assets/avatar.png',
          navbar_logo: '/Assets/logo.png',
          resume_pdf_url: null,
          home_greeting: 'Hi folks!',
          home_name: 'Firas Bayoudhi',
          home_location: 'Tunisia, and Currently resident in Brussels',
          home_title: 'IoT Developer',
          home_quote: 'Success is often achieved by those who don\'t know that failure is inevitable.',
          home_quote_author: 'Coco Chanel, fashion designer',
          home_job_roles: 'Junior IoT Developer,Backend Developer,AI Enthusiast'
        };
        
        setSettings(normalizedData);
        setError(null);
        // Apply colors dynamically when settings are loaded
        applyPortfolioColors(normalizedData);
      } else {
        setError('Failed to fetch portfolio settings');
      }
    } catch (err) {
      console.error('Error fetching portfolio settings:', err);
      setError('Failed to load portfolio settings');
      // Set defaults if API fails
      const defaultSettings = {
        home_main_image: '/Assets/home-main.png',
        home_about_image: '/Assets/about.png',
        about_avatar_image: '/Assets/avatar.png',
        navbar_logo: '/Assets/logo.png',
        resume_pdf_url: null,
        home_greeting: 'Hi folks!',
        home_name: 'Firas Bayoudhi',
        home_location: 'Tunisia, and Currently resident in Brussels',
        home_title: 'IoT Developer',
        home_quote: 'Success is often achieved by those who don\'t know that failure is inevitable.',
        home_quote_author: 'Coco Chanel, fashion designer',
        primary_color: '#4e69c3',
        secondary_color: '#5e87f5',
        button_color: '#3d4b91',
        button_hover_color: '#4e69c3',
        quote_color: '#5e87f5',
        tech_icon_border_color: '#4e69c3',
        project_card_shadow_color: '#5e87f5',
        scrollbar_color: '#b279d8',
        scrollbar_hover_color: '#de82eb'
      };
      setSettings(defaultSettings);
      applyPortfolioColors(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    setRefreshKey(prev => prev + 1);
  };

  return { settings, loading, error, refetch };
}

